import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import { useBranding } from "../../contexts/BrandingContext";
import { useBusinessLabels } from "../../hooks/useBusinessLabels";
import { BUSINESS_TYPE_OPTIONS } from "../../config/businessLabels";
import {
  COLOR_THEME_PRESETS,
  findMatchingPresetId,
  getDefaultThemeForBusiness,
  getSuggestedThemePresets,
} from "../../config/colorThemes";
import { applyBrandTheme } from "../../utils/applyBrandTheme";
import { ImageCropper } from "../../components/admin/ImageCropper";
import * as api from "../../services/api";
import { resolveHomeSubtitle } from "../../utils/resolveHomeSubtitle";
import styles from "./OnboardingPage.module.css";
import {
  ArrowRight,
  Upload,
  Loader,
  Image as ImageIcon,
} from "lucide-react";

const TOTAL_STEPS = 6;

const OnboardingPage = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { refreshBranding } = useBranding();
  const { artist, organization, user } = useAuth();
  const businessType = organization?.businessType ?? "OTHER";
  const labels = useBusinessLabels(businessType);

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [tempImage, setTempImage] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState("");

  const [homeTitle, setHomeTitle] = useState("");
  const [homeSubtitle, setHomeSubtitle] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [primaryColor, setPrimaryColor] = useState("#0369A1");
  const [secondaryColor, setSecondaryColor] = useState("#0EA5E9");
  const [selectedThemeId, setSelectedThemeId] = useState<string | null>("ocean");
  const [invoiceServiceName, setInvoiceServiceName] = useState("");
  const [receiptSkipped, setReceiptSkipped] = useState(false);

  const organizationId = artist?.id;
  const themePresets = getSuggestedThemePresets(businessType);

  const applyThemeColors = useCallback((primary: string, secondary: string): void => {
    setPrimaryColor(primary);
    setSecondaryColor(secondary);
    applyBrandTheme({ primaryColor: primary, secondaryColor: secondary });
  }, []);

  const handleThemeSelect = (themeId: string): void => {
    const preset = COLOR_THEME_PRESETS.find((p) => p.id === themeId);
    if (!preset) return;
    setSelectedThemeId(themeId);
    applyThemeColors(preset.primaryColor, preset.secondaryColor);
  };

  const handlePrimaryChange = (value: string): void => {
    setPrimaryColor(value);
    setSelectedThemeId(findMatchingPresetId(value, secondaryColor));
    applyBrandTheme({ primaryColor: value, secondaryColor });
  };

  const handleSecondaryChange = (value: string): void => {
    setSecondaryColor(value);
    setSelectedThemeId(findMatchingPresetId(primaryColor, value));
    applyBrandTheme({ primaryColor, secondaryColor: value });
  };

  useEffect(() => {
    const load = async (): Promise<void> => {
      if (!organizationId) {
        setLoading(false);
        return;
      }
      try {
        const data = await api.getBranding(organizationId);
        if (data?.onboardingCompleted) {
          navigate("/admin/dashboard", { replace: true });
          return;
        }
        const defaultTheme = getDefaultThemeForBusiness(businessType);
        const primary = data?.primaryColor || defaultTheme.primaryColor;
        const secondary = data?.secondaryColor || defaultTheme.secondaryColor;

        setHomeTitle(data?.homeTitle || organization?.name || "");
        setHomeSubtitle(
          resolveHomeSubtitle(
            data?.homeSubtitle,
            businessType,
            organization?.name || "",
            t,
            labels.joinTitle,
          ),
        );
        setLogoUrl(data?.logoUrl || "");
        applyThemeColors(primary, secondary);
        setSelectedThemeId(findMatchingPresetId(primary, secondary) ?? defaultTheme.id);
        setInvoiceServiceName(data?.invoiceServiceName || labels.service);
      } catch {
        const defaultTheme = getDefaultThemeForBusiness(businessType);
        setHomeTitle(organization?.name || "");
        setHomeSubtitle(
          resolveHomeSubtitle(
            undefined,
            businessType,
            organization?.name || "",
            t,
            labels.joinTitle,
          ),
        );
        applyThemeColors(defaultTheme.primaryColor, defaultTheme.secondaryColor);
        setSelectedThemeId(defaultTheme.id);
        setInvoiceServiceName(labels.service);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [
    organizationId,
    navigate,
    labels.joinTitle,
    labels.service,
    businessType,
    organization?.name,
    applyThemeColors,
    t,
  ]);

  const buildBrandingPayload = (completed = false): Record<string, unknown> => {
    const payload: Record<string, unknown> = {
      homeTitle,
      homeSubtitle,
      primaryColor,
      secondaryColor,
      onboardingCompleted: completed,
    };
    if (logoUrl) {
      payload.logoUrl = logoUrl;
    }
    if (!receiptSkipped && invoiceServiceName.trim()) {
      payload.invoiceServiceName = invoiceServiceName.trim();
    }
    return payload;
  };

  const saveProgress = async (completed = false): Promise<void> => {
    if (!organizationId) return;
    setSaving(true);
    try {
      await api.updateBranding(organizationId, buildBrandingPayload(completed));
      if (completed) {
        await refreshBranding();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async (): Promise<void> => {
    if (step < TOTAL_STEPS) {
      await saveProgress(false);
      setStep((s) => s + 1);
      return;
    }
    await saveProgress(true);
    navigate("/admin/events/create?tutorial=1");
  };

  const handleSkipLogo = async (): Promise<void> => {
    await saveProgress(false);
    setStep(4);
  };

  const handleSkipReceipt = async (): Promise<void> => {
    setReceiptSkipped(true);
    setInvoiceServiceName("");
    await saveProgress(false);
    setStep(6);
  };

  const handleSkipToDashboard = async (): Promise<void> => {
    await saveProgress(true);
    navigate("/admin/dashboard");
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError("");
    const reader = new FileReader();
    reader.onload = () => {
      setTempImage(reader.result as string);
      setCropModalOpen(true);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleCropComplete = async (croppedBlob: Blob): Promise<void> => {
    if (!organizationId) return;
    setCropModalOpen(false);
    setUploading(true);
    setUploadError("");
    try {
      const file = new File([croppedBlob], "logo.png", { type: "image/png" });
      const url = await api.uploadLogo(organizationId, file);
      setLogoUrl(url);
    } catch {
      setUploadError(t("admin.onboarding.logoUploadError"));
    } finally {
      setUploading(false);
      setTempImage(null);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>{t("common.loading")}</div>
      </div>
    );
  }

  const businessLabel = BUSINESS_TYPE_OPTIONS.find((o) => o.value === businessType);

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.header}>
          <h1 className={styles.title}>{t("admin.onboarding.title")}</h1>
          <p className={styles.subtitle}>
            {t("admin.onboarding.step", { current: step, total: TOTAL_STEPS })}
          </p>
        </div>

        {step === 1 && (
          <div className={styles.stepBody}>
            <p className="text-lg font-semibold">
              {t("admin.onboarding.welcomeTitle", {
                name: user?.name || organization?.name || "",
              })}
            </p>
            <p>{t("admin.onboarding.welcomeText")}</p>
            <div className={styles.welcomeHighlight}>
              <p className={styles.welcomeHighlightLabel}>
                {t("admin.onboarding.businessType")}
              </p>
              <p className={styles.welcomeHighlightValue}>
                {businessLabel ? t(businessLabel.labelKey) : businessType}
              </p>
              {businessLabel && (
                <p className={styles.welcomeHighlightDesc}>
                  {t(businessLabel.descriptionKey)}
                </p>
              )}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className={styles.stepBody}>
            <div>
              <h2 className="font-semibold">{t("admin.onboarding.identityTitle")}</h2>
              <p className="mt-1">{t("admin.onboarding.identitySubtitle")}</p>
            </div>
            <div>
              <label className={styles.label} htmlFor="homeSubtitle">
                {t("admin.onboarding.subtitleLabel")}
              </label>
              <input
                id="homeSubtitle"
                type="text"
                value={homeSubtitle}
                onChange={(e) => setHomeSubtitle(e.target.value)}
                className={styles.input}
                placeholder={t("admin.onboarding.subtitlePlaceholder")}
              />
            </div>
          </div>
        )}

        {step === 3 && (
          <div className={styles.stepBody}>
            <div>
              <h2 className="font-semibold">{t("admin.onboarding.logoTitle")}</h2>
              <p className="mt-1">{t("admin.onboarding.logoSubtitle")}</p>
            </div>
            <div className={styles.logoRow}>
              <div className={styles.logoPreview}>
                {logoUrl ? (
                  <img
                    src={api.getAssetUrl(logoUrl)}
                    alt=""
                    className={styles.logoPreviewImg}
                  />
                ) : (
                  <ImageIcon className="w-6 h-6 text-gray-300" aria-hidden="true" />
                )}
              </div>
              <label className={styles.uploadBtn}>
                {uploading ? (
                  <Loader className="w-4 h-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Upload className="w-4 h-4" aria-hidden="true" />
                )}
                {logoUrl
                  ? t("admin.onboarding.logoChange")
                  : t("admin.onboarding.logoUpload")}
                <input
                  type="file"
                  className={styles.hiddenInput}
                  accept="image/*"
                  onChange={handleLogoUpload}
                  disabled={uploading}
                />
              </label>
            </div>
            {uploadError && <p className="text-red-600 text-xs">{uploadError}</p>}
            {logoUrl && (
              <p className="text-xs text-green-700">{t("admin.onboarding.logoUploaded")}</p>
            )}
          </div>
        )}

        {step === 4 && (
          <div className={styles.stepBody}>
            <div>
              <h2 className="font-semibold">{t("admin.onboarding.themeTitle")}</h2>
              <p className="mt-1">{t("admin.onboarding.themeSubtitle")}</p>
            </div>
            <div className={styles.themeGrid}>
              {themePresets.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  className={`${styles.themeCard} ${
                    selectedThemeId === preset.id ? styles.themeCardSelected : ""
                  }`}
                  onClick={() => handleThemeSelect(preset.id)}
                >
                  <div
                    className={styles.themePreview}
                    style={{ backgroundColor: preset.primaryColor }}
                  />
                  <div className={styles.themeMeta}>
                    <span className={styles.themeName}>
                      {t(`admin.onboarding.themes.${preset.id}`)}
                    </span>
                    <div className={styles.themeSwatch}>
                      <span
                        className={styles.swatch}
                        style={{ backgroundColor: preset.primaryColor }}
                      />
                      <span
                        className={styles.swatch}
                        style={{ backgroundColor: preset.secondaryColor }}
                      />
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <p className={styles.label}>{t("admin.onboarding.themeCustom")}</p>
            <div className={styles.colorPickers}>
              <div>
                <label className={styles.label}>{t("admin.branding.primary")}</label>
                <div className={styles.colorPickerRow}>
                  <input
                    type="color"
                    value={primaryColor}
                    onChange={(e) => handlePrimaryChange(e.target.value)}
                    className={styles.colorInput}
                    aria-label={t("admin.branding.primary")}
                  />
                  <input
                    type="text"
                    value={primaryColor}
                    onChange={(e) => handlePrimaryChange(e.target.value)}
                    className={styles.colorHex}
                  />
                </div>
              </div>
              <div>
                <label className={styles.label}>{t("admin.branding.secondary")}</label>
                <div className={styles.colorPickerRow}>
                  <input
                    type="color"
                    value={secondaryColor}
                    onChange={(e) => handleSecondaryChange(e.target.value)}
                    className={styles.colorInput}
                    aria-label={t("admin.branding.secondary")}
                  />
                  <input
                    type="text"
                    value={secondaryColor}
                    onChange={(e) => handleSecondaryChange(e.target.value)}
                    className={styles.colorHex}
                  />
                </div>
              </div>
            </div>
            <div className={styles.previewWrap}>
              <p className={styles.previewLabel}>{t("admin.branding.preview")}</p>
              <div className={styles.previewCard}>
                <div className={styles.previewLogo}>
                  {logoUrl ? (
                    <img
                      src={api.getAssetUrl(logoUrl)}
                      alt=""
                      className={styles.logoPreviewImg}
                    />
                  ) : (
                    <ImageIcon className="w-4 h-4 text-gray-300" aria-hidden="true" />
                  )}
                </div>
                <p className={styles.previewTitle} style={{ color: primaryColor }}>
                  {homeTitle || organization?.name}
                </p>
                <p className={styles.previewSubtitle}>{homeSubtitle}</p>
                <div className={styles.previewBtn} />
              </div>
            </div>
          </div>
        )}

        {step === 5 && (
          <div className={styles.stepBody}>
            <div>
              <h2 className="font-semibold">
                {t("admin.onboarding.receiptTitle")}
                <span className={styles.optionalBadge}>
                  {t("admin.onboarding.optional")}
                </span>
              </h2>
              <p className="mt-1">{t("admin.onboarding.receiptSubtitle")}</p>
              <p className="mt-2 text-xs">{t("admin.onboarding.receiptOptionalHint")}</p>
            </div>
            <div>
              <label className={styles.label} htmlFor="invoiceServiceName">
                {t("admin.onboarding.serviceNameLabel")}
              </label>
              <input
                id="invoiceServiceName"
                type="text"
                value={invoiceServiceName}
                onChange={(e) => {
                  setReceiptSkipped(false);
                  setInvoiceServiceName(e.target.value);
                }}
                className={styles.input}
                placeholder={labels.service}
              />
            </div>
          </div>
        )}

        {step === 6 && (
          <div className={styles.readyBlock}>
            <h2 className={styles.readyTitle}>{t("admin.onboarding.readyTitle")}</h2>
            <p className={styles.readyText}>{t("admin.onboarding.readyText")}</p>
          </div>
        )}

        <div className={styles.actions}>
          <button
            type="button"
            onClick={handleNext}
            disabled={saving || uploading}
            className={styles.button}
          >
            {saving ? (
              t("common.loading")
            ) : step === TOTAL_STEPS ? (
              t("admin.onboarding.createEvent")
            ) : (
              <>
                {t("admin.onboarding.next")}
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </>
            )}
          </button>

          {step === 3 && (
            <button
              type="button"
              onClick={handleSkipLogo}
              disabled={saving || uploading}
              className={styles.secondaryBtn}
            >
              {t("admin.onboarding.logoSkip")}
            </button>
          )}

          {step === 5 && (
            <button
              type="button"
              onClick={handleSkipReceipt}
              disabled={saving}
              className={styles.secondaryBtn}
            >
              {t("admin.onboarding.receiptSkip")}
            </button>
          )}

          {step === TOTAL_STEPS && (
            <button
              type="button"
              onClick={handleSkipToDashboard}
              disabled={saving}
              className={styles.linkBtn}
            >
              {t("admin.onboarding.skipToDashboard")}
            </button>
          )}
        </div>
      </div>

      {cropModalOpen && tempImage && (
        <ImageCropper
          image={tempImage}
          onCropComplete={handleCropComplete}
          onClose={() => {
            setCropModalOpen(false);
            setTempImage(null);
          }}
        />
      )}
    </div>
  );
};

export default OnboardingPage;
